import { CONDUCT_AXES, CONDUCT_AXIS_COLUMN } from '../../conduct/conduct-types';
import { ConductAxisField } from '../../conduct/ConductAxisField';
import { ConductAxisReadout } from '../../conduct/ConductAxisReadout';
import {
  axisResolution,
  useEffectiveConductProfile,
  usePersonaConductUsage,
} from '../../conduct/use-conduct-profile';

import type { PersonaDto } from '../types';
import type { PersonaForm } from '../use-persona-form';

/**
 * Seção «Conduta» do editor de Persona — Fase 2 «Arquitetura de Conduta e Contexto».
 *
 * Nível MAIS ESPECÍFICO da cascata: quando a persona opina, ela vence playbook e tenant.
 *
 * ── ★ O problema que só existe nesta tela: a cadeia BIFURCA ───────────────────────────
 * Uma persona pode ser apontada por VÁRIOS playbooks (medido no dev:
 * `persona_canonical_tenant_netcomet_suporte-tecnico` é apontada por 3). Então:
 *
 *   - se a persona OPINA num eixo, ela vence em todos os playbooks ⇒ o efetivo é único e
 *     honesto de mostrar;
 *   - se ela NÃO opina, o valor efetivo depende de QUAL playbook está conduzindo ⇒ não
 *     existe «um» efetivo, e mostrar um número seria mentira.
 *
 * Por isso a seção resolve a cascata ancorada num playbook concreto (o primeiro que usa esta
 * persona) e NOMEIA os demais, em vez de fingir uma resposta única. Sem playbook ativo
 * apontando, avisa que a persona não está em uso — o que ela configurar aqui não afeta
 * ninguém hoje.
 *
 * Exporta leitura e edição (molde das outras `Section*{Read,Edit}` do editor).
 */

function ConductBody({
  persona,
  form,
  editable,
}: {
  readonly persona: PersonaDto;
  readonly form: PersonaForm | null;
  readonly editable: boolean;
}): JSX.Element {
  const usage = usePersonaConductUsage(persona.id);
  const playbooks = usage.data?.playbooks ?? [];
  // Âncora: a cascata precisa de UM playbook para ser determinada. O primeiro basta —
  // a persona é o nível mais específico, então o que ela opina vale em todos.
  const anchor = playbooks[0];
  const effective = useEffectiveConductProfile(anchor?.id);
  const levels = effective.data?.levels;

  return (
    <section className="max-w-2xl">
      <header className="mb-4">
        <h3 className="text-base font-semibold text-text-primary">Conduta desta persona</h3>
        <p className="mt-1 text-sm text-text-secondary">
          Ajustes de comportamento que valem quando a IA fala com esta voz. Este é o nível mais
          específico: o que você definir aqui vence o playbook e a configuração do tenant.
        </p>

        {usage.isSuccess && playbooks.length === 0 ? (
          <p className="mt-2 rounded-lg bg-app-bg px-3 py-2 text-xs text-text-secondary">
            Nenhum playbook ativo usa esta persona hoje. O que você configurar aqui fica guardado,
            mas não afeta nenhum atendimento enquanto um playbook não apontar para ela.
          </p>
        ) : null}

        {playbooks.length > 1 ? (
          <p className="mt-2 rounded-lg bg-app-bg px-3 py-2 text-xs text-text-secondary">
            Esta persona é usada por <strong>{playbooks.length} playbooks</strong> (
            {playbooks.map((p) => p.name).join(', ')}). Onde você opinar aqui, o valor vale nos{' '}
            {playbooks.length}. Onde herdar, o valor pode <strong>variar por playbook</strong> — os
            valores herdados abaixo são os de <strong>{anchor?.name}</strong>.
          </p>
        ) : null}
      </header>

      <div className="space-y-4">
        {CONDUCT_AXES.map((axis) => {
          const column = CONDUCT_AXIS_COLUMN[axis];
          const value = form === null ? persona[column] : form.draft[column];
          // Leitura = TEXTO, nunca `<select disabled>` (contrato das Section*Read irmãs,
          // travado por `personas-pr3.spec.tsx`).
          if (form === null || !editable) {
            return (
              <ConductAxisReadout
                key={axis}
                axis={axis}
                level="persona"
                resolution={axisResolution(effective.data, axis)}
                levels={levels}
                value={value ?? null}
              />
            );
          }
          return (
            <ConductAxisField
              key={axis}
              axis={axis}
              level="persona"
              resolution={axisResolution(effective.data, axis)}
              levels={levels}
              value={value ?? null}
              onChange={(next) => {
                form.set(column, next as never);
              }}
            />
          );
        })}
      </div>
    </section>
  );
}

/** Leitura — seletores desabilitados, origem visível. */
export function SectionConductRead({ persona }: { readonly persona: PersonaDto }): JSX.Element {
  return <ConductBody persona={persona} form={null} editable={false} />;
}

/** Edição — gated por `persona-author` no chamador (`PersonaEditorView`). */
export function SectionConductEdit({
  persona,
  form,
}: {
  readonly persona: PersonaDto;
  readonly form: PersonaForm;
}): JSX.Element {
  return <ConductBody persona={persona} form={form} editable />;
}
